// src/pages/dashboard/WhitelistApplications.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useDarkMode } from "../../context/DarkModeContext";
import { useApplication } from "../../context/ApplicationContext";
import {
  Search,
  //   Filter,
  Download,
  Mail,
  Calendar,
  FileText,
  User,
  Building,
  MapPin,
  //  Clock,
  CheckCircle,
  XCircle,
  Clock4,
  Eye,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from "lucide-react";

interface Application {
  id: string;
  jobId: string;
  userId: string;
  status: "pending" | "reviewed" | "accepted" | "rejected" | "withdrawn";
  coverLetter?: string;
  resumeUrl?: string;
  createdAt: string;
  updatedAt: string;
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    salary: string;
    type: string;
    category: string;
    deadline?: string;
    createdAt: string;
    postedBy: {
      id: string;
      name: string;
      email: string;
    };
  };
  user?: {
    id: string;
    name: string;
    email: string;
    profile?: {
      phone?: string;
      location?: string;
      bio?: string;
    };
  };
}

export default function WhitelistApplications() {
  const { user } = useAuth();
  const { isDark } = useDarkMode();
  const {
    applications,
    loading,
    error,
    refreshApplications,
    updateApplicationStatus,
  } = useApplication();

  const [filteredApplications, setFilteredApplications] = useState<
    Application[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("newest");
  const [expandedApplication, setExpandedApplication] = useState<string | null>(
    null,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check user permissions
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isWhitelisted =
    user?.subscriptionState?.subscriptions?.isWhitelisted ||
    user?.subscriptionState?.whitelist?.isActive ||
    user?.isWhitelisted;

  // Filter applications based on user role
  const myJobApplications = useMemo(() => {
    if (!user) return [];

    if (isAdmin) {
      // Admins can see all applications
      return applications;
    } else if (isWhitelisted) {
      // Whitelisted users can only see applications for jobs they posted
      return applications.filter((app) => app.job.postedBy.id === user.id);
    }

    return [];
  }, [applications, user, isAdmin, isWhitelisted]);

  // Apply filters and search
  useEffect(() => {
    let filtered = myJobApplications;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((app) => app.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.job.title.toLowerCase().includes(term) ||
          app.job.company.toLowerCase().includes(term) ||
          app.user?.name.toLowerCase().includes(term) ||
          app.user?.email.toLowerCase().includes(term) ||
          app.coverLetter?.toLowerCase().includes(term),
      );
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "oldest":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "company":
          return a.job.company.localeCompare(b.job.company);
        case "applicant":
          return (a.user?.name || "").localeCompare(b.user?.name || "");
        default:
          return 0;
      }
    });

    setFilteredApplications(filtered);
  }, [myJobApplications, statusFilter, searchTerm, sortBy]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshApplications();
    setIsRefreshing(false);
  };

  const handleStatusUpdate = async (
    applicationId: string,
    newStatus: Application["status"],
  ) => {
    try {
      await updateApplicationStatus(applicationId, newStatus);
    } catch (err) {
      console.error("Failed to update application status:", err);
    }
  };

  const toggleApplicationExpand = (applicationId: string) => {
    setExpandedApplication(
      expandedApplication === applicationId ? null : applicationId,
    );
  };

  const getStatusIcon = (status: Application["status"]) => {
    switch (status) {
      case "pending":
        return <Clock4 className="w-4 h-4 text-yellow-500" />;
      case "reviewed":
        return <Eye className="w-4 h-4 text-blue-500" />;
      case "accepted":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "withdrawn":
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock4 className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: Application["status"]) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800";
      case "reviewed":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800";
      case "accepted":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800";
      case "withdrawn":
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const downloadResume = (resumeUrl: string, applicantName: string) => {
    const link = document.createElement("a");
    link.href = resumeUrl;
    link.download = `${applicantName.replace(/\s+/g, "_")}_Resume.pdf`;
    link.click();
  };

  if (!isAdmin && !isWhitelisted) {
    return (
      <div className="p-6">
        <div
          className={`rounded-lg border p-8 text-center ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2
            className={`text-xl font-semibold mb-2 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Access Denied
          </h2>
          <p className={`mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            You need to be a whitelisted employer or administrator to view
            applications.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1
                className={`text-2xl sm:text-3xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {isAdmin ? "All Job Applications" : "My Job Applications"}
              </h1>
              <p
                className={`mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                {isAdmin
                  ? "Manage and review all applications across the platform"
                  : "Manage and review applications for your job postings"}
              </p>
            </div>

            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isRefreshing
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-sky-500 hover:bg-sky-600 text-white"
              }`}
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard
            title="Total Applications"
            value={myJobApplications.length}
            icon={<FileText className="w-5 h-5" />}
            isDark={isDark}
          />
          <StatCard
            title="Pending Review"
            value={
              myJobApplications.filter((app) => app.status === "pending").length
            }
            icon={<Clock4 className="w-5 h-5" />}
            isDark={isDark}
            className="text-yellow-600 dark:text-yellow-400"
          />
          <StatCard
            title="Under Review"
            value={
              myJobApplications.filter((app) => app.status === "reviewed")
                .length
            }
            icon={<Eye className="w-5 h-5" />}
            isDark={isDark}
            className="text-blue-600 dark:text-blue-400"
          />
          <StatCard
            title="Accepted"
            value={
              myJobApplications.filter((app) => app.status === "accepted")
                .length
            }
            icon={<CheckCircle className="w-5 h-5" />}
            isDark={isDark}
            className="text-green-600 dark:text-green-400"
          />
          <StatCard
            title="Not Selected"
            value={
              myJobApplications.filter((app) => app.status === "rejected")
                .length
            }
            icon={<XCircle className="w-5 h-5" />}
            isDark={isDark}
            className="text-red-600 dark:text-red-400"
          />
        </div>

        {/* Filters and Search */}
        <div
          className={`mb-6 p-4 rounded-lg ${
            isDark ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Under Review</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Not Selected</option>
              <option value="withdrawn">Withdrawn</option>
            </select>

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="company">Company</option>
              <option value="applicant">Applicant Name</option>
            </select>
          </div>
        </div>

        {/* Applications List */}
        <div className="space-y-4">
          {loading ? (
            // Loading State
            <div
              className={`rounded-lg p-8 text-center ${
                isDark ? "bg-gray-800" : "bg-white"
              }`}
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto"></div>
              <p
                className={`mt-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Loading applications...
              </p>
            </div>
          ) : error ? (
            // Error State
            <div
              className={`rounded-lg border p-8 text-center ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3
                className={`text-lg font-semibold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Error Loading Applications
              </h3>
              <p
                className={`mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                {error}
              </p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredApplications.length === 0 ? (
            // Empty State
            <div
              className={`rounded-lg border p-8 text-center ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3
                className={`text-lg font-semibold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                No Applications Found
              </h3>
              <p
                className={`mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                {searchTerm || statusFilter !== "all"
                  ? "No applications match your current filters. Try adjusting your search criteria."
                  : isAdmin
                    ? "No applications have been submitted yet."
                    : "You haven't received any applications for your job postings yet."}
              </p>
            </div>
          ) : (
            // Applications List
            filteredApplications.map((application) => (
              <ApplicationCard
                key={application.id}
                application={application}
                isDark={isDark}
                isExpanded={expandedApplication === application.id}
                onToggleExpand={toggleApplicationExpand}
                onStatusUpdate={handleStatusUpdate}
                onDownloadResume={downloadResume}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
                formatDate={formatDate}
                isAdmin={isAdmin}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Helper Components
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  isDark: boolean;
  className?: string;
}

function StatCard({
  title,
  value,
  icon,
  isDark,
  className = "",
}: StatCardProps) {
  return (
    <div
      className={`p-4 rounded-lg border transition-colors ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p
            className={`text-sm font-medium ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {title}
          </p>
          <p
            className={`text-2xl font-bold mt-1 ${className} ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {value}
          </p>
        </div>
        <div
          className={`p-2 rounded-full ${
            isDark ? "bg-gray-700" : "bg-gray-100"
          } ${className}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

interface ApplicationCardProps {
  application: Application;
  isDark: boolean;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onStatusUpdate: (id: string, status: Application["status"]) => void;
  onDownloadResume: (url: string, name: string) => void;
  getStatusIcon: (status: Application["status"]) => React.ReactNode;
  getStatusColor: (status: Application["status"]) => string;
  formatDate: (date: string) => string;
  isAdmin: boolean;
}

function ApplicationCard({
  application,
  isDark,
  isExpanded,
  onToggleExpand,
  onStatusUpdate,
  onDownloadResume,
  getStatusIcon,
  getStatusColor,
  formatDate,
  isAdmin,
}: ApplicationCardProps) {
  return (
    <div
      className={`rounded-lg border transition-all duration-200 ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      } ${isExpanded ? "ring-2 ring-sky-500" : ""}`}
    >
      {/* Application Header */}
      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {application.job.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center gap-1 text-sm ${
                      isDark ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <Building className="w-4 h-4" />
                    {application.job.company}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 text-sm ${
                      isDark ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <MapPin className="w-4 h-4" />
                    {application.job.location}
                  </span>
                  {isAdmin && (
                    <span
                      className={`inline-flex items-center gap-1 text-sm ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      <User className="w-4 h-4" />
                      Posted by: {application.job.postedBy.name}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${getStatusColor(
                      application.status,
                    )}`}
                  >
                    {getStatusIcon(application.status)}
                    {application.status.charAt(0).toUpperCase() +
                      application.status.slice(1)}
                  </span>
                </div>
              </div>

              <button
                onClick={() => onToggleExpand(application.id)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <strong>Applicant:</strong> {application.user?.name}
                </p>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <strong>Email:</strong> {application.user?.email}
                </p>
                {application.user?.profile?.phone && (
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <strong>Phone:</strong> {application.user.profile.phone}
                  </p>
                )}
              </div>
              <div>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <strong>Applied:</strong> {formatDate(application.createdAt)}
                </p>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <strong>Last Updated:</strong>{" "}
                  {formatDate(application.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Applicant Details */}
              <div className="lg:col-span-1">
                <h4
                  className={`font-semibold mb-3 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Applicant Information
                </h4>
                <div
                  className={`space-y-3 p-4 rounded-lg ${
                    isDark ? "bg-gray-700" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {application.user?.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div>
                      <p
                        className={`font-medium ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {application.user?.name}
                      </p>
                      <p
                        className={`text-sm ${
                          isDark ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {application.user?.email}
                      </p>
                    </div>
                  </div>

                  {application.user?.profile?.location && (
                    <p
                      className={`text-sm flex items-center gap-2 ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      <MapPin className="w-4 h-4" />
                      {application.user.profile.location}
                    </p>
                  )}

                  {application.user?.profile?.phone && (
                    <p
                      className={`text-sm flex items-center gap-2 ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      <PhoneIcon className="w-4 h-4" />
                      {application.user.profile.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Cover Letter */}
              <div className="lg:col-span-2">
                <h4
                  className={`font-semibold mb-3 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Cover Letter
                </h4>
                <div
                  className={`p-4 rounded-lg ${
                    isDark ? "bg-gray-700" : "bg-gray-50"
                  }`}
                >
                  {application.coverLetter ? (
                    <p
                      className={`whitespace-pre-wrap ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {application.coverLetter}
                    </p>
                  ) : (
                    <p
                      className={`italic ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No cover letter provided.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {/* Status Update */}
              <select
                value={application.status}
                onChange={(e) =>
                  onStatusUpdate(
                    application.id,
                    e.target.value as Application["status"],
                  )
                }
                className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="pending">Pending Review</option>
                <option value="reviewed">Under Review</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Not Selected</option>
                <option value="withdrawn">Withdrawn</option>
              </select>

              {/* Resume Download */}
              {application.resumeUrl && (
                <button
                  onClick={() =>
                    onDownloadResume(
                      application.resumeUrl!,
                      application.user?.name || "Applicant",
                    )
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download Resume
                </button>
              )}

              {/* Contact Applicant */}
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                <Mail className="w-4 h-4" />
                Contact
              </button>

              {/* Schedule Interview */}
              <button className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors">
                <Calendar className="w-4 h-4" />
                Schedule Interview
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Phone Icon component
function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}
